import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { researchService } from "../services/research.service";
import { ResearchChatMessage, ResearchChatData } from "../types/research.types";
import { applyResearchReply, isPendingResearchTurn } from "./research-chat.utils";

export const useSendResearchMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: researchService.sendMessage,
    mutationKey: ["sendResearchMessage"],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["chat", variables.chat_id] });
      const previousChat = queryClient.getQueryData<ResearchChatData>(["chat", variables.chat_id]);
      const optimisticMessage: ResearchChatMessage = {
        question: variables.data,
        response: "",
        creation_date: new Date().toISOString(),
        related_questions: [],
        status: "pending",
      };
      queryClient.setQueryData<ResearchChatData>(["chat", variables.chat_id], {
        prev_chat: [...(previousChat?.prev_chat ?? []), optimisticMessage],
      });
      return { previousChat };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ResearchChatData>(["chat", variables.chat_id], (oldData) =>
        applyResearchReply(oldData, variables.data, data)
      );
    },
    onError: (_error, variables, context) => {
      toast.error("Couldn't send that research question. Please try again.", {
        id: "chat-send-failed",
      });
      queryClient.setQueryData<ResearchChatData>(
        ["chat", variables.chat_id],
        context?.previousChat ?? { prev_chat: [] }
      );
    },
    onSettled: (_data, error, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["chats"] });
      const chat = queryClient.getQueryData<ResearchChatData>(["chat", variables.chat_id]);
      const last = chat?.prev_chat[chat.prev_chat.length - 1];
      // A timed-out POST can still have saved the turn on the server. Refetch
      // so that row appears under the prompt instead of arriving later above
      // the next question.
      if (error != null || isPendingResearchTurn(last)) {
        void queryClient.invalidateQueries({ queryKey: ["chat", variables.chat_id] });
      }
    },
  });
};
